import defaults from "lodash/defaults";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import {
  getPermissionNamesByIds,
  getRoleNameById,
} from "../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import AddContract from "./modals/add-contract";
import CreateQuestionnaire from "./modals/create-questionnaire";
import DeleteAutomation from "./modals/delete-automation";
import IntegrateYourApp from "./modals/integrate-your-app";
import LinkProgram from "./modals/link-program";
import NewAutomation from "./modals/new-automation";
import SaveAsTemplate from "./modals/save-as-template";
import SelectDailyAccountabilityTemplates from "./modals/select-daily-accountability-templates";
import SelectGuides from "./modals/select-guides";
import SelectMacroCalculator from "./modals/select-macro-calculator";
import SelectMealPlanLibrary from "./modals/select-meal-plan-library";
import SelectVideoPlaylist from "./modals/select-video-playlist";
import SetYourGoals from "./modals/set-your-goals";
import SetupEmail from "./modals/setup-email";
import StartFromTemplate from "./modals/start-from-template";
import TurnOffAutomation from "./modals/turn-off-automation";
import TurnOnAutomation from "./modals/turn-on-automation";
import WelcomeVideo from "./modals/welcome-video";
import { CoachDailyMonitoring } from "./screens/CoachDailyMonitoring";
import CustomWorkflows from "./screens/CustomWorkflows";
import { GettingStarted } from "./screens/GettingStarted";

const CustomWorkflowsController = async ({ res, req, locals, load }) => {
  const { query = {} } = req;
  const modal = query.modal || {};
  const {
    activeUser,
    organizationOwner,
    organizationRoles,
    organizationPermissions: globalOrganizationPermissions = [],
  } = locals;
  const { organizationId, organizationPermissions = [] } = activeUser;
  //User's permissions (if it's part of an organization, if not, we only are giving a empty array)
  const userOrganizationPermission =
    organizationPermissions.length > 0
      ? getPermissionNamesByIds(
          globalOrganizationPermissions,
          organizationPermissions
        )
      : [""];
  const roleName = activeUser.organizationRoleId
    ? getRoleNameById(organizationRoles, activeUser.organizationRoleId)
    : "Owner";

  const userToBindInformation =
    roleName === "Owner" ? activeUser : organizationOwner;

  const isPushCoach = Boolean(userToBindInformation.pushAccountInfo);
  const userCanAccessToPush =
    (!organizationId && isPushCoach) ||
    (isPushCoach &&
      !!organizationId &&
      userOrganizationPermission.includes("PUSH"));
  const userOrganizationCanAccessToWorkflows =
    !organizationId ||
    (!!organizationId &&
      userOrganizationPermission.includes("Custom Workflows"));

  // Email Automations pilot: only accounts with the flag see its screens
  const emailAutomationsEnabled = Boolean(
    userToBindInformation.emailAutomationsEnabled
  );
  if (emailAutomationsEnabled) {
    await load("sendingSettings");
    await load("automationsList");
    locals.emailProducts = locals.automationsList?.products ?? [];
  }

  const ownWorkflowsAccess =
    userCanAccessToPush && userOrganizationCanAccessToWorkflows
      ? "push"
      : "pull";
  // An account that only has PRESS or PERFORM starts on its first product
  const firstAccess =
    !userCanAccessToPush && locals.emailProducts?.length > 0
      ? locals.emailProducts[0]
      : ownWorkflowsAccess;
  defaults(query, { access: firstAccess });
  const access = query.access ?? "push";

  await load("workflows");
  if (emailAutomationsEnabled && locals.emailProducts?.includes(access)) {
    await load("automationFilterOptions");
    // The empty state of PUSH and PULL says how many templates there are to start from
    const hasAutomations = locals.automationsList?.automations?.some(
      (automation) => automation.product === access
    );
    if (["push", "pull"].includes(access) && !hasAutomations) {
      await load("automationTemplates", access);
    }
  }

  if (query.workflow && locals.workflows) {
    locals.workflow = locals.workflows.find(
      ({ _id }) => _id === query.workflow
    );
  }

  // The confirmations of an automation read it (and how many people are inside) first
  if (
    emailAutomationsEnabled &&
    [
      "turnOffAutomation",
      "turnOnAutomation",
      "deleteAutomation",
      "saveAsTemplate",
    ].includes(modal.type) &&
    query.automation
  ) {
    await load("automationDetail", query.automation);
  }
  if (emailAutomationsEnabled && modal.type === "saveAsTemplate") {
    await load(
      "automationTemplates",
      locals.automationDetail?.automation.product
    );
  }
  if (emailAutomationsEnabled && modal.type === "newAutomation") {
    await load("automationTriggers");
    await load("automationFilterOptions");
  }
  if (emailAutomationsEnabled && modal.type === "turnOnAutomation") {
    await load("automationBackfill", query.automation);
    await load(
      "automationTriggers",
      locals.automationDetail?.automation.product
    );
    await load("automationFilterOptions");
  }
  if (emailAutomationsEnabled && modal.type === "automationTemplates") {
    await load("automationTemplates", query.product || access);
    await load("automationTriggers", query.product || access);
  }
  const automationReturnTo = `/coaching-portal/custom-workflows/?access=${access}&view=email`;

  switch (modal.type) {
    case "macroCalculator":
      await load("macroCalculators", { access });
      break;
    case "videos":
      await load("videoPlaylistWidgets", { access });
      break;
    case "guides":
      await load("guidesWidgets", { access });
      break;
    case "mealPlan":
      await load("mealPlanWidgets", { access });
      break;
    case "accountabilities":
      await load("accountabilityWidgets", { access });
      break;
    default:
      break;
  }

  const sections = {
    main: (
      <CustomWorkflows
        userCanAccessToPush={userCanAccessToPush}
        userOrganizationCanAccessToWorkflows={
          userOrganizationCanAccessToWorkflows
        }
      />
    ),
    "getting-started": <GettingStarted />,
    "coach-daily-monitoring": <CoachDailyMonitoring />,
  };

  locals.modal = {
    welcomeVideo: <WelcomeVideo workflow={locals.workflow} />,
    program: (
      <LinkProgram
        workflow={locals.workflow}
        explanationAdded={query.explanationAdded}
      />
    ),
    goals: (
      <SetYourGoals
        workflow={locals.workflow}
        explanationAdded={query.explanationAdded}
      />
    ),
    integrations: <IntegrateYourApp workflow={locals.workflow} />,
    macroCalculator: (
      <SelectMacroCalculator
        workflow={locals.workflow}
        explanationAdded={query.explanationAdded}
        macroCalculators={locals.macroCalculators}
      />
    ),
    videos: (
      <SelectVideoPlaylist
        workflow={locals.workflow}
        videoPlaylistWidgets={locals.videoPlaylistWidgets}
      />
    ),
    guides: (
      <SelectGuides
        workflow={locals.workflow}
        explanationAdded={query.explanationAdded}
        guidesWidgets={locals.guidesWidgets}
      />
    ),
    mealPlan: (
      <SelectMealPlanLibrary
        workflow={locals.workflow}
        mealPlanWidgets={locals.mealPlanWidgets}
        explanationAdded={query.explanationAdded}
      />
    ),
    accountabilities: (
      <SelectDailyAccountabilityTemplates
        workflow={locals.workflow}
        accountabilityWidgets={locals.accountabilityWidgets}
        explanationAdded={query.explanationAdded}
      />
    ),
    contract: <AddContract workflow={locals.workflow} />,
    questionnaire: (
      <CreateQuestionnaire workflow={locals.workflow} locals={locals} />
    ),
    "activity-feed": <ActivityDrawer />,
    newAutomation:
      emailAutomationsEnabled && locals.automationTriggers ? (
        <NewAutomation
          triggers={locals.automationTriggers.triggers}
          categories={locals.automationTriggers.categories}
          products={locals.emailProducts ?? []}
          options={locals.automationFilterOptions ?? undefined}
          product={query.product || access}
        />
      ) : null,
    turnOnAutomation:
      emailAutomationsEnabled && locals.automationDetail ? (
        <TurnOnAutomation
          detail={locals.automationDetail}
          backfill={locals.automationBackfill ?? undefined}
          definition={locals.automationTriggers?.triggers.find(
            (trigger) =>
              trigger.key === locals.automationDetail.automation.trigger.key
          )}
          options={locals.automationFilterOptions ?? undefined}
          returnTo={automationReturnTo}
        />
      ) : null,
    automationTemplates:
      emailAutomationsEnabled && locals.automationTemplates ? (
        <StartFromTemplate
          templates={locals.automationTemplates.templates}
          mine={locals.automationTemplates.mine ?? []}
          teamName={locals.automationTemplates.teamName}
          triggers={locals.automationTriggers?.triggers ?? []}
          options={locals.automationFilterOptions ?? undefined}
          product={query.product || access}
        />
      ) : null,
    saveAsTemplate:
      emailAutomationsEnabled && locals.automationDetail ? (
        <SaveAsTemplate
          detail={locals.automationDetail}
          teamName={locals.automationTemplates?.teamName ?? ""}
          returnTo={automationReturnTo}
        />
      ) : null,
    turnOffAutomation: emailAutomationsEnabled ? (
      <TurnOffAutomation
        detail={locals.automationDetail}
        returnTo={automationReturnTo}
      />
    ) : null,
    deleteAutomation: emailAutomationsEnabled ? (
      <DeleteAutomation
        detail={locals.automationDetail}
        returnTo={automationReturnTo}
      />
    ) : null,
    sendingSettings: emailAutomationsEnabled ? (
      <SetupEmail
        settings={locals.sendingSettings}
        ownerEmail={userToBindInformation.email}
      />
    ) : null,
  }[modal.type];

  res.body = sections[query.type] || sections.main;
};

export default CustomWorkflowsController;
